import { usePrepop } from '@hooks';
import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { Link } from '@spark-web/link';
import { Stack, StackProps } from '@spark-web/stack';
import { BRIGHTE_ASSETS_URL, BRIGHTE_SITE_DOMAIN } from '@utils/constants';
import { getTrackingId } from '@utils/helper';
import LogoImage from '../../assets/logo.svg?react';

type HeaderProps = {
  position?: StackProps['alignSelf'];
  partnerId?: string;
};

const env = import.meta.env.VITE_ENVIRONMENT as string;
const BRIGHTE_PARTNER_ID = 'brighte';
const DEFAULT_LOGO_HEIGHT = 40;

// Reject anything that isn't a valid https:// URL — blocks non-HTTP(S) schemes
// such as `javascript:`/`data:` from a partner-config-driven href.
const isSafeHttpsUrl = (url: string | null | undefined): url is string => {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

export function Header({ position, partnerId }: HeaderProps) {
  const trackingId = getTrackingId();
  const { data } = usePrepop({ trackingId });
  const partnerWebsiteUrl = data?.enterprisePartnerConfig?.partnerWebsiteUrl ?? null;
  const brighteSiteSlug = data?.enterprisePartnerConfig?.brighteSiteSlug ?? null;
  const partnerName = data?.enterprisePartnerConfig?.partnerName ?? 'Partner';
  const hasCoBrandedLogo = Boolean(data?.enterprisePartnerConfig?.hasCoBrandedLogo);

  const brighteSiteDomain = BRIGHTE_SITE_DOMAIN[env] ?? BRIGHTE_SITE_DOMAIN['staging'];
  const fallbackPartnerUrl = brighteSiteSlug
    ? `https://${brighteSiteDomain}/${brighteSiteSlug}`
    : `https://${brighteSiteDomain}`;
  const partnerUrl = isSafeHttpsUrl(partnerWebsiteUrl) ? partnerWebsiteUrl : fallbackPartnerUrl;

  const showCoBrandedLogo = hasCoBrandedLogo && Boolean(partnerId);
  const showPartnerLogo = !showCoBrandedLogo && Boolean(partnerId) && partnerId !== BRIGHTE_PARTNER_ID;
  const headerPosition = showCoBrandedLogo || showPartnerLogo ? 'start' : 'center';

  return (
    <Stack gap="xlarge" paddingY="xxlarge" as="header">
      <Box flex={1} justifyContent={position ?? headerPosition} display="flex" gap="medium">
        {showCoBrandedLogo ? (
          <Box display="flex" gap="medium" data-testid="partner-logo">
            <Link href={partnerUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={`${BRIGHTE_ASSETS_URL}/marketplace/partners/${partnerId}-partnership-logo.svg`}
                alt={`${partnerName} co-branded logo`}
                height={DEFAULT_LOGO_HEIGHT}
              />
            </Link>
          </Box>
        ) : (
          <>
            <Link
              href={`https://${brighteSiteDomain}`}
              aria-label="Brighte homepage"
              target="_blank"
              rel="noopener noreferrer"
              data={{ testId: 'logo' }}
            >
              <LogoImage aria-label="Brighte logo" />
            </Link>
            {showPartnerLogo && (
              <Box display="flex" gap="medium" data-testid="partner-logo">
                <Divider vertical />
                <Link href={partnerUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={`${BRIGHTE_ASSETS_URL}/marketplace/partners/${partnerId}-logo.svg`}
                    alt={`${partnerName} logo`}
                    height={DEFAULT_LOGO_HEIGHT}
                  />
                </Link>
              </Box>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
